import { LinkifyIt } from 'linkify-it'

export type TTextPart = {
    /** Offset in the source string — unique and stable, so it doubles as the render key. */
    start: number
    text: string
    url?: string
}

// Only explicit http(s) URLs: schemeless text like `audio.com` is prose far more often than a link.
const linkify = new LinkifyIt()
    .set({ fuzzyLink: false, fuzzyEmail: false, fuzzyIP: false })
    .add('mailto:', null)
    .add('ftp:', null)
    .add('//', null)

// Inside a URL these let the visible text disagree with the destination.
const INVISIBLE_CHARS =
    /[\u00AD\u115F\u1160\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\u2800\u3164\uFEFF]/

const isSafeUrl = (url: string, raw: string) => {
    const lowerUrl = url.toLowerCase()

    return (
        (lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://')) &&
        !INVISIBLE_CHARS.test(url) &&
        !INVISIBLE_CHARS.test(raw)
    )
}

/**
 * Splits text into plain and link parts. Boundaries come from `linkify-it`, so trailing
 * punctuation stays out of the href.
 */
export const splitTextByLinks = (text: string): TTextPart[] => {
    const matches = linkify.match(text)

    if (!matches?.length) {
        return [{ start: 0, text }]
    }

    const parts: TTextPart[] = []
    let cursor = 0

    matches.forEach(({ index, lastIndex, raw, url }) => {
        if (index > cursor) {
            parts.push({ start: cursor, text: text.slice(cursor, index) })
        }

        parts.push(
            isSafeUrl(url, raw) ? { start: index, text: raw, url } : { start: index, text: raw },
        )
        cursor = lastIndex
    })

    if (cursor < text.length) {
        parts.push({ start: cursor, text: text.slice(cursor) })
    }

    return parts
}
